// Palworld boss catalogue: Tower Bosses, Raid Bosses and Predator/World bosses.
// Data mirrors Palworld 1.0 (2026) including Sakurajima and Feybreak content.
//
// Artwork note: official boss art is Pocketpair intellectual property, so the
// site renders its own element-tinted procedural illustration (see BossArt).

export type BossKind = "Tower Boss" | "Raid Boss" | "Predator Boss";

export interface Boss {
  slug: string;
  name: string;
  pal: string;
  kind: BossKind;
  element: string;
  color: string;
  accent: string;
  level: number;
  hp: string;
  location: string;
  faction: string;
  weakness: string;
  resists: string;
  timeLimit: string;
  rewards: string[];
  desc: string;
  strategy: string;
  recommendedPals: string[];
}

const C: Record<string, [string, string]> = {
  Electric: ["#fbbf24", "#b45309"],
  Grass: ["#4ade80", "#15803d"],
  Dragon: ["#a78bfa", "#5b21b6"],
  Fire: ["#fb7185", "#b91c1c"],
  Dark: ["#8b5cf6", "#312e81"],
  Ice: ["#67e8f9", "#0e7490"],
  Ground: ["#d6b370", "#92400e"],
  Water: ["#60a5fa", "#1d4ed8"],
  Neutral: ["#cbd5e1", "#475569"],
};

const tint = (element: string) => C[element] ?? C.Neutral;

function boss(b: Omit<Boss, "color" | "accent">): Boss {
  const [color, accent] = tint(b.element);
  return { ...b, color, accent };
}

export const BOSSES: Boss[] = [
  boss({
    slug: "zoe-and-grizzbolt",
    name: "Zoe & Grizzbolt",
    pal: "Grizzbolt",
    kind: "Tower Boss",
    element: "Electric",
    level: 10,
    hp: "~25,000",
    location: "Rayne Syndicate Tower, Windswept Hills (coords 114, -434)",
    faction: "Rayne Syndicate",
    weakness: "Ground",
    resists: "Electric, Water",
    timeLimit: "10 minutes",
    rewards: ["Ancient Technology Points ×5", "Zoe's Trust (achievement)", "Access to Ancient Tech recipes"],
    desc: "The first Tower Boss in Palworld. Zoe rides Grizzbolt, an Electric-type gatling gunner, and acts as the game's difficulty check before the mid-game.",
    strategy: "Stay mobile and circle-strafe. Grizzbolt's minigun only fires in a forward cone, so break line of sight behind the arena pillars, then punish the reload window with a Ground-type Pal and a Handgun or Old Bow.",
    recommendedPals: ["Rushoar", "Fuack", "Tanzee"],
  }),
  boss({
    slug: "lily-and-lyleen",
    name: "Lily & Lyleen",
    pal: "Lyleen",
    kind: "Tower Boss",
    element: "Grass",
    level: 30,
    hp: "~60,000",
    location: "Free Pal Alliance Tower, Bamboo Groves (coords -95, -220)",
    faction: "Free Pal Alliance",
    weakness: "Fire",
    resists: "Grass, Ground, Water",
    timeLimit: "10 minutes",
    rewards: ["Ancient Technology Points ×5", "Lily's Trust", "Mid-game Ancient Tech unlocks"],
    desc: "Lily fights alongside Lyleen, a Grass-type healer that regenerates health and spawns lingering damage zones across the arena floor.",
    strategy: "Bring sustained Fire damage — a Flamethrower or Ragnahawk out-paces Lyleen's regeneration. Never stand in the green pollen circles, and keep an Ice or Water Pal on standby if you need to reset aggro.",
    recommendedPals: ["Ragnahawk", "Arsox", "Incineram"],
  }),
  boss({
    slug: "axel-and-orserk",
    name: "Axel & Orserk",
    pal: "Orserk",
    kind: "Tower Boss",
    element: "Dragon",
    level: 40,
    hp: "~80,000",
    location: "Brothers of the Eternal Pyre Tower, Dessicated Desert (coords 357, 353)",
    faction: "Brothers of the Eternal Pyre",
    weakness: "Ice, Ground",
    resists: "Dragon, Electric, Water",
    timeLimit: "10 minutes",
    rewards: ["Ancient Technology Points ×5", "Axel's Trust", "Late-game Ancient Tech unlocks"],
    desc: "Orserk is a Dragon/Electric hybrid with a full-arena lightning barrage, making Axel the first Tower Boss that punishes low mobility outright.",
    strategy: "Fight from range with an Ice weapon or an Ice Pal such as Frostallion Noct, and dodge sideways when the arena floor flashes. Heat-resistant armour plus Mega Health Potions cover the chip damage between phases.",
    recommendedPals: ["Frostallion", "Mammorest Cryst", "Astegon"],
  }),
  boss({
    slug: "marcus-and-faleris",
    name: "Marcus & Faleris",
    pal: "Faleris",
    kind: "Tower Boss",
    element: "Fire",
    level: 50,
    hp: "~100,000",
    location: "PIDF Tower, Astral Mountains (coords -669, 542)",
    faction: "PIDF",
    weakness: "Water",
    resists: "Fire, Grass, Ice",
    timeLimit: "10 minutes",
    rewards: ["Ancient Technology Points ×5", "Marcus's Trust", "Endgame Ancient Tech unlocks"],
    desc: "Marcus commands Faleris, a Fire-type raptor that dives across the arena and leaves burning trails behind every pass.",
    strategy: "Use Water damage and Heat-resistant Pal armour. Faleris telegraphs each dive with a hover, so sprint perpendicular to its path, then unload during the landing recovery with an Assault Rifle or Jormuntide.",
    recommendedPals: ["Jormuntide", "Suzaku Aqua", "Penking"],
  }),
  boss({
    slug: "victor-and-shadowbeak",
    name: "Victor & Shadowbeak",
    pal: "Shadowbeak",
    kind: "Tower Boss",
    element: "Dark",
    level: 55,
    hp: "~130,000",
    location: "PAL Genetic Research Unit Tower, No Man's Trail (coords -150, -400)",
    faction: "PAL Genetic Research Unit",
    weakness: "Dragon",
    resists: "Dark, Neutral",
    timeLimit: "10 minutes",
    rewards: ["Ancient Technology Points ×5", "Victor's Trust", "Final story unlock"],
    desc: "The final story Tower Boss. Shadowbeak is a Dark-type flier with homing projectile volleys and a hard-hitting melee slam.",
    strategy: "Dragon damage is mandatory — Jetragon or a Legendary Rocket Launcher shortens the fight dramatically. Keep three Mega Shield rolls in reserve for the volley phase and heal only while Shadowbeak is airborne.",
    recommendedPals: ["Jetragon", "Jormuntide Ignis", "Paladius"],
  }),
  boss({
    slug: "bjorn-and-bastigor",
    name: "Bjorn & Bastigor",
    pal: "Bastigor",
    kind: "Tower Boss",
    element: "Ice",
    level: 55,
    hp: "~140,000",
    location: "Sakurajima Tower, Sakurajima Island",
    faction: "Moonflowers",
    weakness: "Fire",
    resists: "Ice, Water",
    timeLimit: "10 minutes",
    rewards: ["Ancient Technology Points ×5", "Bjorn's Trust", "Sakurajima Ancient Tech"],
    desc: "The Sakurajima Tower Boss added with the island expansion. Bastigor is an Ice-type charger that freezes the arena floor and applies stacking Chill.",
    strategy: "Stack Cold Resistance, bring Fire damage, and never fight from the frozen rings — Chill stacks slow your dodge roll. Kite in wide circles and burst during Bastigor's post-charge stagger.",
    recommendedPals: ["Blazamut", "Suzaku", "Faleris"],
  }),
  boss({
    slug: "saya-and-selyne",
    name: "Saya & Selyne",
    pal: "Selyne",
    kind: "Tower Boss",
    element: "Dark",
    level: 60,
    hp: "~160,000",
    location: "Feybreak Tower, Feybreak Island",
    faction: "Feybreak researchers",
    weakness: "Dragon",
    resists: "Dark, Neutral",
    timeLimit: "10 minutes",
    rewards: ["Ancient Technology Points ×5", "Saya's Trust", "Feybreak endgame unlocks"],
    desc: "The hardest Tower Boss in Palworld 1.0. Selyne combines Dark-type homing orbs with a full-arena moonlight nova on a fixed timer.",
    strategy: "Treat the nova as a DPS gate: if the boss is not below half health by the second cast, disengage and re-gear. Dragon Pals, Legendary weapons and fully condensed 4-star party members are the realistic entry bar.",
    recommendedPals: ["Jetragon", "Xenolord (post-clear)", "Necromus"],
  }),
  boss({
    slug: "bellanoir",
    name: "Bellanoir",
    pal: "Bellanoir",
    kind: "Raid Boss",
    element: "Dark",
    level: 50,
    hp: "~200,000",
    location: "Summoning Altar at your own base",
    faction: "Raid summon",
    weakness: "Dragon",
    resists: "Dark, Neutral",
    timeLimit: "15 minutes",
    rewards: ["Bellanoir Slab fragments", "Ancient Civilization Parts", "Chance to capture Bellanoir"],
    desc: "Palworld's first raid boss, summoned at a Summoning Altar with a Slab instead of being found in the world. Designed for a full four-Pal party or a co-op group.",
    strategy: "Summon at a purpose-built flat arena base with no walls to block movement. Rotate four Dragon-type Pals so each one tanks a phase, and keep the altar behind you so adds cannot cut off your escape lane.",
    recommendedPals: ["Jetragon", "Astegon", "Necromus"],
  }),
  boss({
    slug: "bellanoir-libero",
    name: "Bellanoir Libero",
    pal: "Bellanoir Libero",
    kind: "Raid Boss",
    element: "Dark",
    level: 55,
    hp: "~350,000",
    location: "Summoning Altar (Libero Slab required)",
    faction: "Raid summon",
    weakness: "Dragon",
    resists: "Dark, Neutral, Ice",
    timeLimit: "15 minutes",
    rewards: ["Legendary Sniper Rifle schematic", "Ancient Civilization Parts ×20+", "Bellanoir Libero capture chance"],
    desc: "The awakened form of Bellanoir and the intended co-op endgame fight. Higher health, faster casts and an enrage phase in the final third.",
    strategy: "Bring a dedicated healer role: one player on Mega Health Potions and Ancient Defence gear while the rest run Legendary Assault Rifles. Break the enrage with staggers from a Grade-4 Pal rather than trying to out-heal it.",
    recommendedPals: ["Jetragon", "Frostallion Noct", "Paladius"],
  }),
  boss({
    slug: "xenolord",
    name: "Xenolord",
    pal: "Xenolord",
    kind: "Raid Boss",
    element: "Dragon",
    level: 60,
    hp: "~500,000",
    location: "Summoning Altar (Xenolord Slab, Sakurajima research)",
    faction: "Raid summon",
    weakness: "Ice",
    resists: "Dragon, Fire, Electric",
    timeLimit: "20 minutes",
    rewards: ["Legendary Shield Mk II schematic", "Ancient Civilization Parts ×30", "Xenolord capture chance"],
    desc: "The highest-health raid boss in Palworld 1.0, a mechanised Dragon that alternates between beam sweeps and armour phases.",
    strategy: "Ice damage plus armour-break windows. Assign one player to interrupt the beam channel while the others focus the exposed core, and never group up — the sweep hits everything in a line.",
    recommendedPals: ["Frostallion", "Mammorest Cryst", "Jormuntide"],
  }),
  boss({
    slug: "blazamut-ryu",
    name: "Blazamut Ryu",
    pal: "Blazamut Ryu",
    kind: "Predator Boss",
    element: "Fire",
    level: 60,
    hp: "~420,000",
    location: "Sakurajima volcanic region (roaming spawn)",
    faction: "Predator Pal",
    weakness: "Water",
    resists: "Fire, Grass, Ice",
    timeLimit: "None (roaming)",
    rewards: ["Blazamut Ryu capture chance", "Ancient Civilization Parts", "High-tier Fire drops"],
    desc: "A Predator Pal that roams Sakurajima with vastly inflated stats compared to its standard counterpart, functioning as an open-world boss encounter.",
    strategy: "Fight it near water and use Water Pals plus a Rocket Launcher. Predator Pals cannot be staggered easily, so treat every attack as unblockable and rely on distance and Heat-resistant armour.",
    recommendedPals: ["Jormuntide", "Suzaku Aqua", "Penking"],
  }),
  boss({
    slug: "jetragon-alpha",
    name: "Alpha Jetragon",
    pal: "Jetragon",
    kind: "Predator Boss",
    element: "Dragon",
    level: 50,
    hp: "~90,000",
    location: "Mount Obsidian summit, No Man's Trail (coords -792, -535)",
    faction: "Field Alpha",
    weakness: "Ice",
    resists: "Dragon, Fire, Electric",
    timeLimit: "None (respawns daily)",
    rewards: ["Legendary Rocket Launcher schematic", "Jetragon capture (fastest flying mount)", "Ancient Civilization Parts"],
    desc: "The most valuable field Alpha in Palworld: Jetragon is the fastest flying mount in the game and drops the Legendary Rocket Launcher schematic.",
    strategy: "Snipe from a nearby ridge with Ice ammo, land Giga Spheres between its hover phases, and bring at least 15 Legendary Spheres — capture rate at full health is punishing, so bring it under 20% first.",
    recommendedPals: ["Frostallion", "Astegon", "Mammorest Cryst"],
  }),
  boss({
    slug: "panthalus-alpha",
    name: "Alpha Panthalus",
    pal: "Panthalus",
    kind: "Predator Boss",
    element: "Water",
    level: 55,
    hp: "~110,000",
    location: "Feybreak coastal waters and Sea Breeze Archipelago shallows (roaming Alpha)",
    faction: "Field Alpha",
    weakness: "Electric",
    resists: "Water, Fire, Ice",
    timeLimit: "None (respawns daily)",
    rewards: ["Panthalus capture chance (Water flying mount)", "Ancient Civilization Parts", "High-tier Water drops"],
    desc: "Alpha Panthalus is the Feybreak-era Water Alpha and Paldeck No. 203: an XL Water Pal with 200 base Defense that doubles as a rideable flying mount and base sky guardian via Resonant Guardian.",
    strategy: "Fight from a cliff or boat with Electric ammo, keep out of its wave sweeps, and burn the 200 Defense down with Electric partner skills before throwing Legendary Spheres under 20% health.",
    recommendedPals: ["Orserk", "Grizzbolt", "Univolt"],
  }),
];

export const BOSS_KINDS: BossKind[] = ["Tower Boss", "Raid Boss", "Predator Boss"];

export const BOSS_BY_SLUG: Record<string, Boss> = Object.fromEntries(
  BOSSES.map((b) => [b.slug, b]),
);

export const TOWER_BOSSES = BOSSES.filter((b) => b.kind === "Tower Boss");
export const RAID_BOSSES = BOSSES.filter((b) => b.kind === "Raid Boss");

// ---------------------------------------------------------------------------
// Derived helpers used by /bosses (reward filters, FAQ, build presets) and by
// the /damage calculator (boss target modelling).
// ---------------------------------------------------------------------------

export const REWARD_TAGS = [
  "Ancient Tech Points",
  "Schematic",
  "Capture chance",
  "Ancient Civilization Parts",
  "Story unlock",
] as const;
export type RewardTag = (typeof REWARD_TAGS)[number];

/** Buckets a boss's reward list into filterable categories. */
export function rewardTags(b: Boss): RewardTag[] {
  const all = b.rewards.join(" ").toLowerCase();
  const tags: RewardTag[] = [];
  if (all.includes("ancient technology points")) tags.push("Ancient Tech Points");
  if (all.includes("schematic")) tags.push("Schematic");
  if (all.includes("capture")) tags.push("Capture chance");
  if (all.includes("civilization parts")) tags.push("Ancient Civilization Parts");
  if (all.includes("trust") || all.includes("unlock")) tags.push("Story unlock");
  return tags;
}

/** Numeric health pool parsed from the display string (e.g. "~25,000" -> 25000). */
export function bossHp(b: Boss): number {
  return Number(b.hp.replace(/[^0-9]/g, "")) || 0;
}

/** Elements the boss takes extra damage from. */
export function weaknessElements(b: Boss): string[] {
  return b.weakness.split(",").map((s) => s.trim()).filter(Boolean);
}

/** Elements the boss resists. */
export function resistElements(b: Boss): string[] {
  return b.resists.split(",").map((s) => s.trim()).filter(Boolean);
}

/**
 * Damage multiplier a Pal of `element` deals to this boss.
 * Weakness 1.5x, resisted 0.5x, otherwise neutral.
 */
export function bossMultiplier(b: Boss, element: string): number {
  if (weaknessElements(b).includes(element)) return 1.5;
  if (resistElements(b).includes(element)) return 0.5;
  return 1;
}

export interface BossPreset {
  label: string;
  weapon: string;
  ammo: string;
  armour: string;
  accessories: string;
  consumables: string;
  party: string[];
  sphere: string;
}

/** Recommended loadout preset, derived from level, kind and weakness. */
export function bossPreset(b: Boss): BossPreset {
  const w = weaknessElements(b)[0] ?? "Neutral";
  const weapon =
    b.level >= 55
      ? "Legendary Assault Rifle"
      : b.level >= 40
        ? "Pump-Action Shotgun"
        : b.level >= 25
          ? "Assault Rifle"
          : "Handgun";
  const armour =
    b.level >= 55
      ? "Legendary Metal Armor + Legendary Shield Mk II"
      : b.level >= 40
        ? "Refined Metal Armor + Mega Shield"
        : "Pal Metal Armor + Shield";
  const heatCold = ["Fire"].includes(b.element)
    ? "Heat Resistant Pal Metal Armor"
    : ["Ice"].includes(b.element)
      ? "Cold Resistant Pal Metal Armor"
      : armour;
  return {
    label: `${b.name} — Lv ${b.level} ${b.kind.toLowerCase()} preset`,
    weapon,
    ammo: b.level >= 40 ? "Rifle Bullets ×500 + Rocket Launcher ×10" : "Rifle Bullets ×300",
    armour: heatCold,
    accessories: `${w} Amulet / Ring of Mercy + Damage Amulet`,
    consumables:
      b.kind === "Raid Boss"
        ? "Mega Health Potion ×10, Salad ×5, Cake for altar summons"
        : "Mega Health Potion ×6, Grilled meat ×5",
    party: [...b.recommendedPals, `Any 4-star condensed ${w}-type`],
    sphere: b.kind === "Tower Boss" ? "Not capturable" : "Legendary Sphere ×15 (below 20% HP)",
  };
}

/** Visible, per-boss FAQ. Render these exact strings when emitting FAQPage JSON-LD. */
export function bossFaq(b: Boss): { q: string; a: string }[] {
  const w = weaknessElements(b).join(" and ");
  return [
    {
      q: `What is ${b.name} weak to in Palworld?`,
      a: `${b.name} is a level ${b.level} ${b.element}-type ${b.kind.toLowerCase()} and takes extra damage from ${w}-type attacks, while resisting ${b.resists}. Best counter Pals: ${b.recommendedPals.join(", ")}.`,
    },
    {
      q: `Where do you find ${b.name}?`,
      a: `${b.name} is located at ${b.location}. The fight has a ${b.timeLimit.toLowerCase()} limit and roughly ${b.hp} health.`,
    },
    {
      q: `What does ${b.name} drop?`,
      a: `${b.name} rewards ${b.rewards.join(", ")}.`,
    },
    {
      q: `How do you beat ${b.name}?`,
      a: b.strategy,
    },
  ];
}
